import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { habitLevel } from '@goaltree/backend/convex/lib/progression'
import {
  ACCENTS,
  ACCENT_NAMES,
  PREVIEW_W,
  THUMB_W,
  openingKey,
  prevMonth,
} from '@goaltree/backend/convex/lib/shareCards'
import { withShareUtm } from '@goaltree/backend/convex/lib/shareUtm'
import { Dialog, DialogContent, DialogTitle } from '@goaltree/ui/components/dialog'
import { Drawer, DrawerContent, DrawerTitle } from '@goaltree/ui/components/drawer'
import { cn } from '@goaltree/ui/lib/utils'
import { useConvexAuth, useMutation, useQuery } from 'convex/react'
import { ConvexError } from 'convex/values'
import { Download, Link2, Loader2, Share2, Trophy, X } from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { habitDayStreak } from '@/components/inbox-habits'
import { StreakCelebration, type StreakMoment } from '@/components/streak-celebration'
import { useIsMobile } from '@/hooks/use-mobile'
import { todayKey } from '@/lib/day'
import { canNativeShare } from '@/lib/share'
import {
  CELEBRATION_GAP_MS,
  type CardLook,
  type MintedCard,
  STREAK_CELEBRATE_MILESTONES,
  STREAK_SHARE_MILESTONES,
  type ShareCardRequest,
  type ShareCardTarget,
  cardImageUrl,
  onShareCard,
  openShareCard,
} from '@/lib/share-card'
import { taskTitle } from '@/lib/tags'

type Theme = CardLook['theme']
type Look = CardLook
type Minted = MintedCard
type Pick = { key: string; style: string }

const FILE_DEBOUNCE_MS = 250

const keyOf = (x: ShareCardTarget) => `${x.type}:${x.ref}`

// Mounted once in the authenticated layout; opened via openShareCard().
export function ShareCardHost() {
  const [req, setReq] = useState<ShareCardRequest | null>(null)
  const [open, setOpen] = useState(false)
  useEffect(
    () =>
      onShareCard((r) => {
        setReq(r)
        setOpen(true)
      }),
    [],
  )
  return (
    <>
      {req && <ShareCardSheet req={req} open={open} onOpenChange={setOpen} />}
      <ShareMomentWatcher />
    </>
  )
}

function ShareCardSheet({
  req,
  open,
  onOpenChange,
}: {
  req: ShareCardRequest
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const mintMany = useMutation(api.shareCards.mintMany)
  const saveOptions = useMutation(api.shareCards.saveOptions)
  const [cards, setCards] = useState<Record<string, Minted | null>>({})
  const [pick, setPick] = useState<Pick | null>(null)
  const [look, setLook] = useState<Look | null>(null)
  const [shown, setShown] = useState<string | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const asked = useRef(new Set<string>())
  const habitState = useHabitShareState(req.habit, open && !!req.habit)
  const habitPhotos = useQuery(
    api.habitPhotos.forHabits,
    open && req.habit ? { habitIds: [req.habit as Id<'nodes'>], sinceDays: 90 } : 'skip',
  )
  const [photos, setPhotos] = useState(!!req.photos)
  const [coach, setCoach] = useState(true)

  const targets = useMemo(() => {
    const list: ShareCardTarget[] = [{ type: req.type, ref: req.ref }, ...(req.alt ?? [])]
    if (req.habit) {
      if (habitState?.best != null) list.push({ type: 'best', ref: req.habit })
      if (habitState?.beforeAfter) list.push({ type: 'progress', ref: req.habit })
      list.push({ type: 'wrap', ref: thisMonth() })
    }
    return list
  }, [req, habitState?.best, habitState?.beforeAfter])
  const primary = keyOf(req)

  useEffect(() => {
    if (!open) return
    asked.current = new Set()
    setCards({})
    setPick(null)
    setLook(null)
    setShown(null)
    setFile(null)
    setPhotos(!!req.photos)
    setCoach(true)
  }, [open, req])

  // One mint per subject; style/theme/accent ride on the URL, so every
  // thumbnail and re-render reuses these tokens.
  useEffect(() => {
    if (!open) return
    const todo = targets.filter((x) => !asked.current.has(keyOf(x)))
    if (!todo.length) return
    const batch = asked.current
    for (const x of todo) batch.add(keyOf(x))
    mintMany({ targets: todo.map(({ type, ref }) => ({ type, ref })) })
      .then((res) => {
        if (batch !== asked.current) return
        const next = Object.fromEntries(todo.map((x, i) => [keyOf(x), res[i] as Minted | null]))
        if (primary in next && !next[primary]) throw new ConvexError('nothing')
        setCards((c) => ({ ...c, ...next }))
      })
      .catch((e) => {
        if (batch !== asked.current || !todo.some((x) => keyOf(x) === primary)) return
        toast.error(e instanceof ConvexError ? t('shareCard.nothing') : t('shareCard.failed'))
        onOpenChange(false)
      })
  }, [open, targets, primary, mintMany, t, onOpenChange])

  useEffect(() => {
    if (pick) return
    const key = openingKey(primary, targets, cards)
    const c = key && cards[key]
    if (!c) return
    setPick({ key, style: c.options.style })
    setLook({ theme: c.options.theme, accent: c.options.accent })
  }, [cards, primary, targets, pick])

  const options = targets.flatMap((x) => {
    const c = cards[keyOf(x)]
    return c ? c.styles.map((style) => ({ target: x, card: c, style })) : []
  })
  const multiType = new Set(options.map((o) => o.target.type)).size > 1
  const current = pick
    ? options.find((o) => keyOf(o.target) === pick.key && o.style === pick.style)
    : undefined
  const type = current?.target.type ?? req.type
  // Photos can only ride on a habit's streak card or a month card; off unless chosen.
  const photoCard = (type === 'streak' || type === 'wrap') && !!habitPhotos?.length
  const withPhotos = photos && photoCard
  const preview =
    current && look && cardImageUrl(current.card, current.style, look, PREVIEW_W, withPhotos, coach)
  const full =
    current && look && cardImageUrl(current.card, current.style, look, undefined, withPhotos, coach)

  useEffect(() => {
    setFile(null)
    if (!full) return
    let live = true
    // Fetched ahead so Share runs inside the tap (Safari drops the gesture after an await).
    const id = setTimeout(() => {
      fetch(full)
        .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(String(r.status)))))
        .then((b) => {
          if (live) setFile(new File([b], `goaltree-${type}.png`, { type: 'image/png' }))
        })
        .catch(() => {})
    }, FILE_DEBOUNCE_MS)
    return () => {
      live = false
      clearTimeout(id)
    }
  }, [full, type])

  const save = (style: string, l: Look, tt = type) =>
    void saveOptions({ type: tt, options: { style, ...l } }).catch(() => {})

  const choose = (o: (typeof options)[number]) => {
    if (!look) return
    setPick({ key: keyOf(o.target), style: o.style })
    save(o.style, look, o.target.type)
  }

  const restyle = (patch: Partial<Look>) => {
    if (!look || !pick) return
    const next = { ...look, ...patch }
    setLook(next)
    save(pick.style, next)
  }

  const copyLink = async (quiet?: boolean) => {
    if (!current) return
    try {
      await navigator.clipboard.writeText(withShareUtm(current.card.link, 'share_card'))
      if (!quiet) toast.success(t('shareCard.linkCopied'))
    } catch {
      toast.error(t('share.copyFailed'))
    }
  }

  const download = () => {
    if (!file) return
    const url = URL.createObjectURL(file)
    const a = document.createElement('a')
    a.href = url
    a.download = file.name
    a.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  const share = () => {
    if (!file || !current) return
    void navigator.clipboard
      .writeText(withShareUtm(current.card.link, 'share_card'))
      .catch(() => {})
    toast.success(t('shareCard.stickerHint'), { duration: 6000 })
    navigator.share({ files: [file] }).catch(() => {})
  }

  const canShareFile = !!file && canNativeShare(file)
  const optionLabel = (o: (typeof options)[number]) =>
    [
      multiType && t(`shareCard.tab.${o.target.type}`),
      o.card.styles.length > 1 && t(`shareCard.style.${o.style}`),
    ].filter((x): x is string => !!x)

  const seg = (items: { key: string; label: string }[], value: string, on: (k: string) => void) => (
    <div className="inline-flex max-w-full self-start overflow-x-auto rounded-lg border border-border/60 p-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {items.map((i) => (
        <button
          key={i.key}
          type="button"
          aria-pressed={value === i.key}
          onClick={() => on(i.key)}
          className={cn(
            'shrink-0 cursor-pointer whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-semibold transition-colors',
            value === i.key
              ? 'bg-foreground text-background'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {i.label}
        </button>
      ))}
    </div>
  )
  const toggle = (title: string, hint: string, checked: boolean, on: (v: boolean) => void) => (
    <label className="flex cursor-pointer items-center justify-between gap-3">
      <span className="flex flex-col">
        <span className="text-xs font-bold">{title}</span>
        <span className="text-[11px] text-muted-foreground">{hint}</span>
      </span>
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        onChange={(e) => on(e.target.checked)}
        className="peer sr-only"
      />
      <span
        aria-hidden
        className="relative h-6 w-10 shrink-0 rounded-full bg-muted transition-colors peer-checked:bg-primary peer-focus-visible:ring-2 peer-focus-visible:ring-ring/50 after:absolute after:top-[3px] after:left-[3px] after:size-[18px] after:rounded-full after:bg-background after:transition-transform peer-checked:after:translate-x-4"
      />
    </label>
  )
  const label = (s: string) => (
    <span className="font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
      {s}
    </span>
  )

  const Title = isMobile ? DrawerTitle : DialogTitle
  const body = (
    <div className="flex flex-col gap-4">
      <Title className="pr-8 text-base font-extrabold">{t(`shareCard.title.${type}`)}</Title>
      {look && options.length > 1 && (
        <div className="-mx-1 flex gap-2.5 overflow-x-auto px-1 pt-1 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {options.map((o) => {
            const on = o === current
            const [name, sub] = optionLabel(o)
            return (
              <button
                key={`${keyOf(o.target)}:${o.style}`}
                type="button"
                aria-pressed={on}
                aria-label={sub ? `${name} · ${sub}` : name}
                onClick={() => choose(o)}
                className="group flex w-16 shrink-0 cursor-pointer flex-col items-center gap-1"
              >
                <span
                  className={cn(
                    'aspect-[9/16] w-full overflow-hidden rounded-md bg-muted ring-offset-2 ring-offset-background transition-shadow',
                    on
                      ? 'ring-2 ring-foreground'
                      : 'ring-1 ring-foreground/10 group-hover:ring-foreground/40',
                  )}
                >
                  <img
                    src={cardImageUrl(
                      o.card,
                      o.style,
                      look,
                      THUMB_W,
                      photos &&
                        (o.target.type === 'streak' || o.target.type === 'wrap') &&
                        photoCard,
                      coach,
                    )}
                    alt=""
                    loading="eager"
                    className="size-full object-cover"
                  />
                </span>
                <span
                  className={cn(
                    'w-full text-center text-[10px] font-semibold leading-tight',
                    on ? 'text-foreground' : 'text-muted-foreground',
                  )}
                >
                  <span className="block truncate">{name}</span>
                  {sub && <span className="block truncate font-medium opacity-60">{sub}</span>}
                </span>
              </button>
            )
          })}
        </div>
      )}
      <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
        <div className="relative aspect-[9/16] h-[40dvh] max-h-[420px] shrink-0 overflow-hidden rounded-xl bg-muted sm:h-[360px]">
          {current && look && (
            <img
              src={cardImageUrl(current.card, current.style, look, THUMB_W, withPhotos, coach)}
              alt=""
              className="absolute inset-0 size-full object-cover"
            />
          )}
          {preview && (
            <img
              key={preview}
              src={preview}
              alt={t(`shareCard.title.${type}`)}
              onLoad={() => setShown(preview)}
              onError={() => setShown(preview)}
              className={cn(
                'absolute inset-0 size-full object-cover transition-opacity duration-150',
                shown === preview ? 'opacity-100' : 'opacity-0',
              )}
            />
          )}
          {!current ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            shown !== preview && (
              <Loader2 className="absolute top-2 right-2 size-4 animate-spin text-white/80 drop-shadow" />
            )
          )}
        </div>
        {current && look && (
          <div className="flex w-full min-w-0 flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              {label(t('shareCard.theme'))}
              {seg(
                (['dark', 'light'] as const).map((k) => ({
                  key: k,
                  label: t(`shareCard.${k}`),
                })),
                look.theme,
                (theme) => restyle({ theme: theme as Theme }),
              )}
            </div>
            {photoCard &&
              toggle(
                t('shareCard.includePhotos'),
                t('shareCard.includePhotosHint'),
                photos,
                setPhotos,
              )}
            {current.card.coach &&
              toggle(
                t('shareCard.showCoach'),
                t('shareCard.showCoachHint', { name: current.card.coach }),
                coach,
                setCoach,
              )}
            <div className="flex flex-col gap-1.5">
              {label(t('shareCard.accent.label'))}
              <div className="flex flex-wrap gap-2">
                {ACCENT_NAMES.filter(
                  (a) =>
                    a === 'category' ||
                    ACCENTS[a as keyof typeof ACCENTS].toLowerCase() !==
                      current.card.accent.toLowerCase(),
                ).map((a) => (
                  <button
                    key={a}
                    type="button"
                    aria-pressed={look.accent === a}
                    aria-label={t(`shareCard.accent.${a}`)}
                    onClick={() => restyle({ accent: a })}
                    style={{
                      background:
                        a === 'category' ? current.card.accent : ACCENTS[a as keyof typeof ACCENTS],
                    }}
                    className={cn(
                      'size-6 cursor-pointer rounded-full ring-1 ring-foreground/10 ring-offset-2 ring-offset-background transition-shadow',
                      look.accent === a && 'ring-2 ring-foreground',
                    )}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2">
        {canShareFile && (
          <button
            type="button"
            onClick={share}
            className="flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-foreground text-sm font-extrabold text-background transition-opacity hover:opacity-90"
          >
            <Share2 className="size-4" />
            {t('shareCard.share')}
          </button>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            disabled={!file}
            onClick={download}
            className={cn(
              'flex h-10 flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl text-sm font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-50',
              canShareFile
                ? 'border border-border/60 hover:bg-muted'
                : 'bg-foreground text-background hover:opacity-90',
            )}
          >
            <Download className="size-4" />
            {t('shareCard.download')}
          </button>
          <button
            type="button"
            disabled={!current}
            onClick={() => void copyLink()}
            className="flex h-10 flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-border/60 text-sm font-bold transition-colors hover:bg-muted disabled:opacity-50"
          >
            <Link2 className="size-4" />
            {t('shareCard.copyLink')}
          </button>
        </div>
        <p className="text-center text-[11px] text-muted-foreground">{t('shareCard.linkHint')}</p>
      </div>
    </div>
  )

  return isMobile ? (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="mx-auto w-full max-w-lg overflow-hidden data-[vaul-drawer-direction=bottom]:max-h-[94dvh] data-[vaul-drawer-direction=bottom]:rounded-t-2xl">
        <div className="overflow-y-auto px-5 pt-4 pb-[max(env(safe-area-inset-bottom),20px)] text-foreground">
          {body}
        </div>
      </DrawerContent>
    </Drawer>
  ) : (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] max-w-xl overflow-y-auto">{body}</DialogContent>
    </Dialog>
  )
}

const thisMonth = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

// Habit card with the streak / level switch. At 0 the sheet opens on this month
// (or the level) once minted.
export function openHabitShare(habitId: string, streak: number) {
  const streakCard = { type: 'streak' as const, ref: habitId }
  const levelCard = { type: 'level' as const, ref: habitId }
  const [first, second] = streak !== 1 ? [streakCard, levelCard] : [levelCard, streakCard]
  openShareCard({ ...first, alt: [second], habit: habitId })
}

// Toasts a Share action when a check-off lands a habit on a streak milestone or a
// new tree stage, wherever the check-off came from.
function useMarkShown() {
  return useMutation(api.shareNudges.markShown).withOptimisticUpdate((store, { kind, key }) => {
    if (kind === 'celebration') {
      if (store.getQuery(api.shareNudges.celebrationState, {}))
        store.setQuery(api.shareNudges.celebrationState, {}, { lastCelebrationAt: Date.now() })
      return
    }
    for (const { args, value } of store.getAllQueries(api.shareNudges.weeklyRecap)) {
      if (value?.weekStart === key)
        store.setQuery(api.shareNudges.weeklyRecap, args, { ...value, dismissed: true })
    }
  })
}

function ShareMomentWatcher() {
  const { t } = useTranslation()
  const { isAuthenticated } = useConvexAuth()
  const habits = useQuery(api.nodes.getHabits, isAuthenticated ? {} : 'skip')
  const levels = useQuery(api.progression.getMyHabitLevels, isAuthenticated ? {} : 'skip')
  const celebration = useQuery(api.shareNudges.celebrationState, isAuthenticated ? {} : 'skip')
  const unseenBadges = useQuery(api.progression.getUnseenBadges, isAuthenticated ? {} : 'skip')
  const markShown = useMarkShown()
  const prevStreaks = useRef<Map<string, number> | null>(null)
  const prevLevels = useRef<Map<string, number> | null>(null)
  const shown = useRef(new Set<string>())
  // markShown round-trips before celebrationState updates.
  const lastCelebrated = useRef(0)
  const [moment, setMoment] = useState<StreakMoment | null>(null)

  useEffect(() => {
    if (!habits) return
    const now = new Map(
      habits.map((h) => [
        h._id as string,
        habitDayStreak(h.completions, h.recursPeriod, h.recursDays),
      ]),
    )
    const prev = prevStreaks.current
    prevStreaks.current = now
    if (!prev || document.visibilityState !== 'visible') return
    for (const h of habits) {
      const n = now.get(h._id)!
      const key = `streak:${h._id}:${n}`
      if (n <= (prev.get(h._id) ?? n) || !STREAK_SHARE_MILESTONES.includes(n)) continue
      if (shown.current.has(key)) continue
      shown.current.add(key)
      shown.current.add(`level:${h._id}`)
      // A first-time streak badge celebrates (with its own share button) instead.
      if (unseenBadges?.some((b) => b.id === `streak_${n}`)) continue
      const last = Math.max(lastCelebrated.current, celebration?.lastCelebrationAt ?? 0)
      if (
        STREAK_CELEBRATE_MILESTONES.includes(n) &&
        celebration !== undefined &&
        Date.now() - last >= CELEBRATION_GAP_MS
      ) {
        lastCelebrated.current = Date.now()
        void markShown({ kind: 'celebration', key: h._id }).catch(() => {})
        setMoment({ habitId: h._id, n, title: taskTitle(h) })
        continue
      }
      toast.success(t('shareCard.streakToast', { n, title: taskTitle(h) }), {
        duration: 10_000,
        action: {
          label: t('shareCard.shareShort'),
          onClick: () => openShareCard({ type: 'streak', ref: h._id }),
        },
      })
    }
  }, [habits, celebration, unseenBadges, markShown, t])

  useEffect(() => {
    if (!levels || !habits) return
    const now = new Map(levels.map((l) => [l.habitId as string, habitLevel(l.total).level]))
    const prev = prevLevels.current
    prevLevels.current = now
    if (!prev || document.visibilityState !== 'visible') return
    for (const l of levels) {
      const lvl = habitLevel(l.total)
      const before = prev.get(l.habitId)
      if (before === undefined || lvl.level <= before) continue
      // A streak milestone toast for the same check-off already offers sharing.
      if (shown.current.delete(`level:${l.habitId}`)) continue
      const key = `level:${l.habitId}:${lvl.level}`
      if (shown.current.has(key)) continue
      shown.current.add(key)
      const h = habits.find((x) => x._id === l.habitId)
      if (!h) continue
      toast.success(
        t('shareCard.levelToast', {
          title: taskTitle(h),
          level: t(`progression.level.${lvl.name}`),
        }),
        {
          duration: 10_000,
          action: {
            label: t('shareCard.shareShort'),
            onClick: () => openShareCard({ type: 'level', ref: h._id }),
          },
        },
      )
    }
  }, [levels, habits, t])

  return <StreakCelebration moment={moment} onClose={() => setMoment(null)} />
}

export function useHabitShareState(habitId: Id<'nodes'> | string | null | undefined, on = true) {
  return useQuery(
    api.shareCards.habitShareState,
    habitId && on ? { habitId: habitId as Id<'nodes'> } : 'skip',
  )
}

// "New best" chip for best-of-day habits after a log beats the all-time best.
export function NewBestChip({
  habitId,
  className,
}: {
  habitId: Id<'nodes'> | string
  className?: string
}) {
  const { t } = useTranslation()
  const s = useHabitShareState(habitId)
  if (!s?.newBestToday) return null
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        openShareCard({ type: 'best', ref: habitId as string })
      }}
      onPointerDown={(e) => e.stopPropagation()}
      className={cn(
        'inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-full bg-amber-500/15 px-2 py-1 text-[11px] font-bold text-amber-700 transition-colors hover:bg-amber-500/25 dark:text-amber-300',
        className,
      )}
    >
      <Trophy className="size-3" />
      {t('shareCard.newBest')}
      <Share2 className="size-3" />
    </button>
  )
}

const monthName = (month: string, lang: string) =>
  new Date(`${month}-15T12:00:00`).toLocaleDateString(lang, { month: 'long' })

const lastMonthCtaActive = (today: string) => Number(today.slice(8, 10)) <= 7

// "Share your October" during the first week of a month.
export function ShareLastMonthCta({ className }: { className?: string }) {
  const { t, i18n } = useTranslation()
  const today = todayKey()
  if (!lastMonthCtaActive(today)) return null
  const month = prevMonth(today.slice(0, 7))
  return (
    <button
      type="button"
      onClick={() => openShareCard({ type: 'wrap', ref: month })}
      className={cn(
        'flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-accent px-4 py-3 text-sm font-extrabold text-accent-foreground transition-opacity hover:opacity-90',
        className,
      )}
    >
      <Share2 className="size-4" data-icon="inline-start" />
      {t('shareCard.shareMonth', { month: monthName(month, i18n.language) })}
    </button>
  )
}

// Past months with activity, each shareable as a wrap card.
export function MonthlyCards() {
  const { t, i18n } = useTranslation()
  const months = useQuery(api.shareCards.recentMonths)
  const current = todayKey().slice(0, 7)
  const past = (months ?? []).filter((m) => m.month < current).slice(0, 6)
  if (!past.length) return null
  return (
    <section className="rounded-2xl border border-border/60 bg-card/40 p-4">
      <h3 className="text-sm font-bold text-foreground">{t('shareCard.monthlyTitle')}</h3>
      <ul className="mt-2 divide-y divide-border/60">
        {past.map((m) => (
          <li key={m.month} className="flex items-center gap-3 py-2">
            <span className="flex-1 text-sm font-semibold text-foreground">
              {new Date(`${m.month}-15T12:00:00`).toLocaleDateString(i18n.language, {
                month: 'long',
                year: 'numeric',
              })}
            </span>
            <span className="font-mono text-[11px] text-muted-foreground">
              {t('shareCard.activeDays', { count: m.activeDays })}
            </span>
            <button
              type="button"
              onClick={() => openShareCard({ type: 'wrap', ref: m.month })}
              aria-label={t('shareCard.shareShort')}
              className="flex size-8 cursor-pointer items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <Share2 className="size-4" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}

// Streak count on a habit row that opens the share sheet without checking off or
// opening the row.
export function StreakShare({
  habitId,
  streak,
  className,
  children,
}: {
  habitId: string
  streak: number
  className?: string
  children: ReactNode
}) {
  const { t } = useTranslation()
  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation()
  return (
    <button
      type="button"
      aria-label={t('shareCard.shareStreak')}
      title={t('shareCard.shareStreak')}
      onClick={(e) => {
        e.stopPropagation()
        openHabitShare(habitId, streak)
      }}
      onPointerDown={stop}
      onMouseDown={stop}
      onTouchStart={stop}
      onKeyDown={stop}
      className={cn(
        '-mx-1 inline-flex cursor-pointer items-center rounded-md px-1 transition-colors hover:bg-orange-500/10 hover:text-foreground',
        className,
      )}
    >
      {children}
    </button>
  )
}

// "Your week" on Today, sharable as a week card. In the first week of a month the
// month wrap takes the slot instead.
export function WeeklyRecapCard({ className }: { className?: string }) {
  const { t } = useTranslation()
  const today = todayKey()
  const recap = useQuery(api.shareNudges.weeklyRecap, { localDate: today })
  const markShown = useMarkShown()
  const [hidden, setHidden] = useState<string | null>(null)
  if (!recap || recap.dismissed || hidden === recap.weekStart) return null
  const dismiss = () => {
    setHidden(recap.weekStart)
    void markShown({ kind: 'recap', key: recap.weekStart }).catch(() => {})
  }
  const close = (
    <button
      type="button"
      onClick={dismiss}
      aria-label={t('shareCard.week.dismiss')}
      className="absolute top-2 right-2 cursor-pointer text-muted-foreground/50 transition-colors hover:text-foreground"
    >
      <X className="size-4" />
    </button>
  )
  if (lastMonthCtaActive(today))
    return (
      <div className={cn('relative pr-7', className)}>
        <ShareLastMonthCta />
        {close}
      </div>
    )
  return (
    <div
      className={cn(
        'relative rounded-2xl border border-[#ec6489]/30 bg-[#ec6489]/5 p-4',
        className,
      )}
    >
      {close}
      <p className="text-sm font-semibold text-foreground">{t('shareCard.week.title')}</p>
      <p className="mt-1 pr-4 text-xs text-muted-foreground">
        {[
          t('shareCard.week.checkIns', { count: recap.checkIns }),
          t('shareCard.week.days', { count: recap.activeDays }),
          t('shareCard.week.streaks', { count: recap.streaksAlive }),
        ].join(' · ')}
      </p>
      <button
        type="button"
        onClick={() => openShareCard({ type: 'week', ref: recap.weekStart })}
        className="mt-3 inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-[#ec6489] px-4 py-1.5 text-xs font-extrabold text-[#12060a] transition-opacity hover:opacity-90"
      >
        <Share2 className="size-3.5" />
        {t('shareCard.shareShort')}
      </button>
    </div>
  )
}
